import { useEffect, useRef, useState } from 'react';

/**
 * Trailing-only throttle for fast-changing React state. Updates are coalesced
 * and committed from a setTimeout (macrotask), never synchronously from the
 * effect itself.
 *
 * WHY trailing-only: the old leading-edge branch called setThrottled directly
 * inside the effect. During streaming bursts that meant every chunk flush ran
 * render -> effect -> setState -> render inside one continuous task. React
 * counts effect-scheduled updates against its nested-update limit, and heavy
 * consumers (StreamingHtmlPreview reloads an iframe srcDoc per commit) kept
 * the chain alive until React threw "Maximum update depth exceeded" and
 * killed the generation. A setTimeout commit yields to the event loop, which
 * resets React's counter — the loop is structurally impossible.
 *
 * Used by streaming visualization previews to keep the rendered output stable
 * during chunk bursts (preserves iframe scroll, avoids flicker on innerHTML
 * resets, etc.) without dropping the final value when streaming completes.
 */
const MIN_DEFER_MS = 16;

export function useThrottledValue<T>(value: T, ms: number): T {
  const [throttled, setThrottled] = useState(value);
  const valueRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUpdateRef = useRef(0);

  valueRef.current = value;

  useEffect(() => {
    if (timerRef.current) return; // a trailing commit is already scheduled
    const elapsed = Date.now() - lastUpdateRef.current;
    const wait = Math.max(MIN_DEFER_MS, ms - elapsed);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastUpdateRef.current = Date.now();
      setThrottled(valueRef.current);
    }, wait);
  }, [value, ms]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return throttled;
}
